// Universal lens toolbar SHELL (Phase 8, GS_GRAPH_DESIGN.md §11) — the
// navbar-clearing positioner, the glass container with the breathing arwes
// glow (keyframes have one home), and the ref the shell's ResizeObserver
// measures for topInset.
//
// ONE ROW (2026-08-19 restyle, supersedes the controlsRow/selectorRow pair):
// the old split left row 1 mostly air (a dropdown, a stretching search box and
// one icon button) while row 2 fought for width, and cost ~100px of chrome
// over every scene. The shell composes the whole bar into the `row` slot;
// controls size themselves and ONE designated flexer (the search input)
// absorbs the slack. `flex-wrap` is the narrow-viewport escape hatch — the bar
// height is measured, so a wrapped second line propagates to topInset on its
// own instead of clipping.
import React, { forwardRef } from "react";
import { useLayout } from "../../contexts/LayoutContext";

// Gutter math (SIDEBAR_LENS_REWORK 2026-08-21 v2, birr's ruling —
// supersedes the 08-18 closed-only gutter): the sidebar toggle is a 44px
// glass tile that lives BETWEEN the sidebar and the bar in both states, so
// the tile column (16 + 44 + 16 = 76, equal gaps) is ALWAYS reserved; the
// open state just shifts everything right by the sidebar's width:
//   closed → left: 76,        right: 16
//   open   → left: 320 + 76,  right: 16
// The 300ms ease-in-out matches the sidebar slide / the tile's own left
// transition (Main.jsx), so the whole top line moves as one piece.
const TILE_COLUMN = 76;
const GUTTER_BASE = 16;

// Two consumption modes:
// - `row`: the FRAME owns the single-row layout — the shell uses this, so no
//   page's bar can drift from the others'.
// - `children` (raw): the page owns the container's contents outright.
const LensToolbar = forwardRef(({ children, row, rowRef }, ref) => {
  const { leftOpen, LEFT_WIDTH } = useLayout();
  // Closed: reserve the 76px tile column for the floating AnimatedA glyph.
  // Open: the toggle lives INSIDE the sidebar header now (no floating tile),
  // so the bar starts a plain 16px gap right of the sidebar edge.
  const gutterLeft = leftOpen ? LEFT_WIDTH + GUTTER_BASE : TILE_COLUMN;

  // RIGHT-SIDEBAR ruling (SIDEBAR_LENS_REWORK 2026-08-21 v2, birr's
  // ruling): the bar NEVER MOVES for the right aside. The positioner is
  // FIXED to the viewport — right edge pinned at 16px — and the aside
  // slides out UNDER the bar (its top is --lens-topbar-inset, Main.jsx),
  // so they stack vertically and neither covers nor squeezes the other.
  // `fixed` is what escapes LensShell's overflow-hidden root (which
  // silently clipped the 08-19 negative-right overhang): overflow clipping
  // does not apply to fixed-position descendants, and no ancestor of a
  // top-bar route's toolbar has a transform to retarget it — the lens
  // routes AND /threads all use the instant-swap route variants
  // (staticVariants, Main.jsx), which is a load-bearing dependency: an
  // animated route transform would turn `fixed` into "absolute inside the
  // motion.div" for the length of the transition.
  return (
    // top-3: the GS navbar is gone (NAVBAR_REMOVAL_PLAN.md) — 12px breathing
    // gap. Left padding is sidebar-state-aware; right is constant — see the
    // equal-gaps math above.
    <div
      className="pointer-events-none fixed inset-x-0 top-3 z-[1000] transition-[padding] duration-300 ease-in-out"
      style={{
        paddingLeft: gutterLeft,
        paddingRight: GUTTER_BASE,
      }}
    >
      <style>{`
        @keyframes arwes-toolbar-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(14,165,233,0.06),
              0 0 12px 2px rgba(14,165,233,0.08),
              0 0 28px 3px rgba(14,165,233,0.03),
              0 22px 48px rgba(0,0,0,0.35);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(14,165,233,0.12),
              0 0 16px 3px rgba(14,165,233,0.13),
              0 0 40px 5px rgba(14,165,233,0.05),
              0 22px 48px rgba(0,0,0,0.35);
          }
        }
      `}</style>
      <div
        ref={ref}
        className="pointer-events-auto relative w-full rounded-xl border border-mainDivider bg-mainBg/95 p-2.5 font-poppins font-normal text-mainText backdrop-blur-md"
        style={{
          // backgroundImage, NOT the `background` shorthand: the shorthand
          // resets background-color, which was silently killing bg-mainBg/95's
          // base tint and leaving the glass as blur-only — the "washed out"
          // transparency from the prod-blocking feedback. PanelFrame in
          // LensShell already does this correctly; now the bar matches.
          backgroundImage:
            "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(14,165,233,0.06) 0%, transparent 100%)",
          animation: "arwes-toolbar-pulse 5s ease-in-out infinite",
          transition:
            "border-color 0.25s ease-out, background 0.25s ease-out, box-shadow 0.25s ease-out",
        }}
      >
        {row != null && (
          // rowRef: LensShell measures this container's children to decide
          // when the search input splits to its own row (controlled wrap).
          <div ref={rowRef} className="flex min-w-0 flex-wrap items-center gap-2">
            {row}
          </div>
        )}
        {children}
      </div>
    </div>
  );
});
LensToolbar.displayName = "LensToolbar";
export default LensToolbar;
